import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { VueFormError, VueServerMessage, VueServerMessageType } from '@vue/vueframework';
import { UserSearchType } from '../shared/types/UserSearchType.enum';
import { User } from '../shared/types/User';
import { RetrievalType } from '../shared/types/RetrievalType.enum';
import { AppTranslationService } from '../app-translation.service';
import { Client } from '../shared/types/Client';
import { AccountService } from '../shared/services/account.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ControlContainer, NgForm } from '@angular/forms';
import { SessionDataService } from '../shared/services/session-data.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountRecoveryUtil } from '../shared/utils/accountrecovery.utils';

@Component({
  selector: 'app-retrieve-user',
  standalone: false,
  styleUrls: [ './retrieve-user.component.css' ],
  templateUrl: './retrieve-user.component.html',
  viewProviders: [ { provide: ControlContainer, useExisting: NgForm } ]
})
export class RetrieveUserComponent implements OnInit {
  @Input() public retrievalType: RetrievalType;
  @Input() public user: User;
  @Input() public clientCode: string;
  @Input() public messages: VueServerMessage[];
  @Input() public nextAction: () => void;
  @Input() public previousAction: () => void;

  @ViewChild('howForm', { static: true }) public howForm;

  public readonly ACCOUNT_RECOVERY_BUNDLE_NAME = 'AccountRecovery';
  public readonly userNotFoundMsgKey = 'AccountServices.userNotFound';
  public readonly duplicateUserRecordsMsgKey = 'AccountServices.duplicateUserRecords';

  public client: Client;
  public formError = new VueFormError();
  public userSearchType: UserSearchType;
  public userSearchTypeEnum = UserSearchType;
  public retrievalTypeEnum = RetrievalType;

  public translations = {
    byIdentity: '',
    byProfile: '',
    byUsername: '',
    submitFormError: '',
    unknownError: ''
  };

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private appTranslationService: AppTranslationService,
    private sessionDataService: SessionDataService,
    private accountRecoveryUtil: AccountRecoveryUtil
  ) { }

  public ngOnInit(): void {
    this.client = this.route.snapshot.data.client;

    this.userSearchType = this.retrievalType === RetrievalType.PASSWORD ?
      UserSearchType.USERNAME :
      UserSearchType.PROFILE;
    this.getTranslations();
  }

  /**
   * Resets the forms and form data. Called when the candidate selects a different radio button for retrieval mode.
   */
  public resetForm(): void {
    this.messages.length = 0;
    this.formError = new VueFormError();
    this.user = {
      clientStudentID: '',
      email: '',
      firstName: '',
      lastName: '',
      userName: ''
    };
  }

  /**
   * Submits the currently active form. Will handle form validation, error messages, and navigation.
   */
  public submit(): void {
    // clear errors
    this.messages.length = 0;
    if (this.howForm.form.valid === true) {
      this.accountService.lookupUsername(this.user, this.route.snapshot.params.clientCode, this.userSearchType)
        .subscribe((response) => {
          this.sessionDataService.setUser(response);
          this.nextAction();
        }, (err) => this.handleError(err));
    } else {
      if (!this.howForm.form.valid) {
        // find the first field in error
        const pageControls = Object.keys(this.howForm.form.controls);
        let i = 0;
        do {
          if (this.howForm.form.controls[ pageControls[ i ] ].invalid) {
            this.formError.fieldInError = pageControls[ i ];
          }
          i++;
        }
        while (this.formError.fieldInError === '');
      }
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.submitFormError, true, '1')
      );
      this.formError.invalidFormSubmit = true;
    }
  }

  private getTranslations(): void {
    this.translations.byProfile = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'searchType.ByProfile');
    this.translations.byIdentity = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'searchType.ByIdentity', [ this.client.ccidFieldName ]);
    this.translations.byUsername = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'searchType.ByUsername');
    this.translations.submitFormError = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'RetrieveUserComponent.FormContainsErrors');
    this.translations.unknownError = this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME,
      'error.UnknownError');
  }

  private handleError(err: HttpErrorResponse): void {
    this.messages.length = 0;
    if (err.status === 400 || err.status === 404) {
      const errorMessage = this.appTranslationService.getTranslationFromErrorMessage(
        this.addCustomerServiceUrl(err.error),
        this.translations.unknownError
      );
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, errorMessage, true, '1', false, true)
      );
    } else {
      this.messages.push(
        new VueServerMessage(VueServerMessageType.Error, this.translations.unknownError, true, '1')
      );
    }
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  private addCustomerServiceUrl(error: any): any {
    const errorData = Array.isArray(error) ? error[ 0 ] : error;

    // Add customer service link to response parameters when user information not found
    if (errorData.code === this.userNotFoundMsgKey || errorData.code === this.duplicateUserRecordsMsgKey) {
      errorData.parameters.push(
        this.appTranslationService.getTranslation(this.ACCOUNT_RECOVERY_BUNDLE_NAME, 'customerServiceUrl')
      );
    }
    return errorData;
  }
}
